import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  adminApi,
  type Difficulty, type OptionInput, type QuestionInput, type QuestionStatus,
} from "@/lib/adminApi";
import { adminAuth } from "@/lib/adminAuth";
import { canManageQuestions } from "@/lib/roles";

export default function AdminQuestionEditor() {
  const { questionId } = useParams();
  const isNew = !questionId;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ["admin-me"], queryFn: () => adminAuth.me(), retry: false });
  if (!me.isLoading && !canManageQuestions(me.data)) return <Navigate to="/admin" replace />;

  const categories = useQuery({ queryKey: ["categories"], queryFn: () => adminApi.listCategories() });
  const existing = useQuery({
    queryKey: ["question", questionId],
    queryFn: () => adminApi.getQuestion(questionId!),
    enabled: !isNew,
  });

  const [form, setForm] = useState<QuestionInput>({
    externalId: null,
    categoryId: "",
    text: "",
    difficulty: "Easy",
    positiveMarks: 1,
    negativeMarks: 0,
    explanation: null,
    status: "Active",
    options: [
      { text: "", isCorrect: true },
      { text: "", isCorrect: false },
      { text: "", isCorrect: false },
      { text: "", isCorrect: false },
    ],
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (existing.data) {
      setForm({
        externalId: existing.data.externalId,
        categoryId: existing.data.categoryId,
        text: existing.data.text,
        difficulty: existing.data.difficulty,
        positiveMarks: existing.data.positiveMarks,
        negativeMarks: existing.data.negativeMarks,
        explanation: existing.data.explanation,
        status: existing.data.status,
        options: existing.data.options.map((o) => ({ text: o.text, isCorrect: o.isCorrect })),
      });
    }
  }, [existing.data]);

  useEffect(() => {
    if (!form.categoryId && categories.data && categories.data.length > 0) {
      setForm((f) => ({ ...f, categoryId: categories.data[0].id }));
    }
  }, [categories.data]);   // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: () => isNew ? adminApi.createQuestion(form) : adminApi.updateQuestion(questionId!, form),
    onSuccess: (q) => {
      qc.invalidateQueries({ queryKey: ["questions"] });
      qc.invalidateQueries({ queryKey: ["question", q.id] });
      navigate("/admin/questions");
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? "Save failed."),
  });

  const setOption = (i: number, patch: Partial<OptionInput>) =>
    setForm((f) => ({ ...f, options: f.options.map((o, idx) => idx === i ? { ...o, ...patch } : o) }));
  const markCorrect = (i: number) =>
    setForm((f) => ({ ...f, options: f.options.map((o, idx) => ({ ...o, isCorrect: idx === i })) }));
  const addOption = () =>
    form.options.length < 5 && setForm((f) => ({ ...f, options: [...f.options, { text: "", isCorrect: false }] }));
  const removeOption = (i: number) =>
    form.options.length > 2 && setForm((f) => ({
      ...f,
      options: f.options.filter((_, idx) => idx !== i)
        .map((o, idx, arr) => idx === 0 && !arr.some((x) => x.isCorrect) ? { ...o, isCorrect: true } : o),
    }));

  const submit = () => {
    setError(null);
    if (!form.text.trim()) { setError("Question text is required."); return; }
    if (!form.categoryId) { setError("Choose a category."); return; }
    if (form.options.some((o) => !o.text.trim())) { setError("All option texts must be filled."); return; }
    if (form.options.filter((o) => o.isCorrect).length !== 1) { setError("Mark exactly one option correct."); return; }
    save.mutate();
  };

  if (!isNew && existing.isLoading) return <Loading />;

  return (
    <main className="mx-auto max-w-3xl p-6">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-brand-ink">{isNew ? "New question" : "Edit question"}</h1>
        <Link to="/admin/questions" className="text-sm text-brand hover:underline">← Back to list</Link>
      </div>

      <div className="card p-6 space-y-4">
        <Field label="External ID (optional — used by Excel upsert)">
          <input className="field-input" value={form.externalId ?? ""}
            placeholder="e.g. BIO-001"
            onChange={(e) => setForm({ ...form, externalId: e.target.value || null })} />
        </Field>

        <Field label="Category">
          <select className="field-input" value={form.categoryId}
            onChange={(e) => setForm({ ...form, categoryId: e.target.value })}>
            <option value="">Choose…</option>
            {categories.data?.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>

        <Field label="Question text">
          <textarea className="field-input min-h-[6rem]" value={form.text}
            onChange={(e) => setForm({ ...form, text: e.target.value })} />
        </Field>

        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Field label="Difficulty">
            <select className="field-input" value={form.difficulty}
              onChange={(e) => setForm({ ...form, difficulty: e.target.value as Difficulty })}>
              <option>Easy</option><option>Medium</option><option>Hard</option>
            </select>
          </Field>
          <Field label="Status">
            <select className="field-input" value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value as QuestionStatus })}>
              <option>Active</option><option>Draft</option><option>Archived</option>
            </select>
          </Field>
          <Field label="Positive marks">
            <input type="number" step="0.25" className="field-input"
              value={form.positiveMarks ?? ""}
              onChange={(e) => setForm({ ...form, positiveMarks: e.target.value === "" ? null : Number(e.target.value) })} />
          </Field>
          <Field label="Negative marks">
            <input type="number" step="0.25" className="field-input"
              value={form.negativeMarks ?? ""}
              onChange={(e) => setForm({ ...form, negativeMarks: e.target.value === "" ? null : Number(e.target.value) })} />
          </Field>
        </div>

        <div>
          <div className="field-label">Options — pick exactly one correct</div>
          <div className="mt-2 space-y-2">
            {form.options.map((o, i) => (
              <div key={i} className="flex items-center gap-2">
                <input type="radio" name="correct" checked={o.isCorrect} onChange={() => markCorrect(i)} className="accent-brand" />
                <input className="field-input flex-1" placeholder={`Option ${i + 1}`}
                  value={o.text} onChange={(e) => setOption(i, { text: e.target.value })} />
                <button className="text-sm text-brand hover:underline disabled:opacity-40"
                  disabled={form.options.length <= 2}
                  onClick={() => removeOption(i)}>Remove</button>
              </div>
            ))}
          </div>
          <button className="mt-2 text-sm text-brand hover:underline disabled:opacity-40"
            disabled={form.options.length >= 5}
            onClick={addOption}>+ Add option (max 5)</button>
        </div>

        <Field label="Explanation (optional — shown in review)">
          <textarea className="field-input min-h-[3rem]" value={form.explanation ?? ""}
            onChange={(e) => setForm({ ...form, explanation: e.target.value || null })} />
        </Field>

        {error && <div className="text-sm text-brand">{error}</div>}

        <div className="flex justify-end gap-2 pt-2">
          <Link to="/admin/questions" className="btn-secondary">Cancel</Link>
          <button className="btn-primary" onClick={submit} disabled={save.isPending}>
            {save.isPending ? "Saving…" : (isNew ? "Create" : "Save")}
          </button>
        </div>
      </div>
    </main>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="field-label">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
function Loading() { return <main className="grid min-h-[40vh] place-items-center text-brand-ink/60">Loading…</main>; }
